import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, Loader as LoaderIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Logo } from "@/components/layout/PublicLayout";

export default function ForcedPasswordChange() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError(t("Die Passw\u00f6rter stimmen nicht \u00fcberein", "Passwords do not match"));
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/auth/change-password/forced", {
        current_password: current,
        new_password: next,
      });
      if (res.data.must_setup_2fa) {
        // Naechstes Gate: 2FA-Einrichtung
        localStorage.setItem("eleven_token", res.data.enroll_token);
        toast.success(t("Passwort ge\u00e4ndert. Jetzt 2FA einrichten.", "Password changed. Now set up 2FA."));
        navigate("/2fa-einrichten");
        return;
      }
      localStorage.setItem("eleven_token", res.data.access_token);
      localStorage.setItem("eleven_user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      toast.success(t("Passwort ge\u00e4ndert. Willkommen!", "Password changed. Welcome!"));
      navigate("/portal");
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error(t("Zeitfenster abgelaufen. Bitte melden Sie sich erneut an.", "Time window expired. Please sign in again."));
        localStorage.removeItem("eleven_token");
        navigate("/login");
        return;
      }
      setError(err.response?.data?.detail || t("\u00c4nderung fehlgeschlagen", "Change failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen dark-section noise-overlay flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
        <div className="flex justify-center">
          <Logo dark />
        </div>
        <Card className="mt-8 rounded-[20px] border-white/10 bg-white/[0.06] backdrop-blur-md">
          <CardContent className="p-8">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-[12px] bg-[#FF3C20]/12 flex items-center justify-center shrink-0">
                <KeyRound className="h-5 w-5 text-[#FF3C20]" />
              </div>
              <div>
                <h1 className="font-display text-lg font-semibold text-white">{t("Neues Passwort festlegen", "Set a new password")}</h1>
                <p className="text-xs text-white/50">
                  {t("Bitte ersetzen Sie Ihr Initial-Passwort durch ein eigenes, sicheres Passwort.", "Please replace your initial password with your own secure password.")}
                </p>
              </div>
            </div>
            <form onSubmit={submit} className="mt-7 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-white/80">{t("Aktuelles Passwort", "Current password")}</Label>
                <Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
                  className="rounded-[10px] bg-white/5 border-white/15 text-white" data-testid="forced-pw-current-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/80">{t("Neues Passwort", "New password")}</Label>
                <Input type="password" required value={next} onChange={(e) => setNext(e.target.value)}
                  className="rounded-[10px] bg-white/5 border-white/15 text-white" data-testid="forced-pw-new-input" />
                <p className="text-[11px] text-white/40">
                  {t("Min. 10 Zeichen, Gro\u00df- und Kleinbuchstaben sowie eine Zahl.", "Min. 10 characters, upper and lower case letters and a number.")}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/80">{t("Neues Passwort wiederholen", "Repeat new password")}</Label>
                <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="rounded-[10px] bg-white/5 border-white/15 text-white" data-testid="forced-pw-confirm-input" />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="forced-pw-error">{error}</p>}
              <Button type="submit" disabled={busy} className="w-full rounded-[12px] bg-[#FF3C20] text-white hover:bg-[#E8241B] active:scale-[0.98]" data-testid="forced-pw-submit">
                {busy ? <LoaderIcon className="h-4 w-4 animate-spin" /> : t("Passwort \u00e4ndern", "Change password")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
